/**
 * Project navigation — the header every `/projects/:key/*` route wears.
 *
 * Ten project screens existed and two of them — Overview and Settings — could
 * not be reached from anywhere in the product. The board's header offered a
 * single "List view" button, so the only way into a project's own settings was
 * to type the URL. A screen nobody can navigate to is a screen that does not
 * exist.
 *
 * Rendered by `ProjectLayout` above the page's own header, so each screen keeps
 * its title, actions and toolbar and gains the bar without knowing about it.
 */

import { useRef } from 'react';
import { NavLink, useParams } from 'react-router';
import { PERMISSIONS, type Permission } from '@virtuetasks/types';
import { useCan } from '@/features/auth';
import { cn } from '@/lib/utils';

interface TabDef {
  /** Path segment under `/projects/:key/`. */
  to: string;
  label: string;
  /** Any one of these grants is enough to see the tab. */
  anyOf?: Permission[];
  /**
   * Tabs that need room to be worth opening. Timeline and Workload are wide
   * charts; on a phone they are a horizontal scroll of nothing useful, so
   * below 1024px they are not offered at all.
   */
  wideOnly?: boolean;
}

const TABS: TabDef[] = [
  { to: 'overview', label: 'Overview' },
  { to: 'board', label: 'Board' },
  { to: 'list', label: 'List' },
  { to: 'calendar', label: 'Calendar' },
  { to: 'timeline', label: 'Timeline', wideOnly: true },
  { to: 'workload', label: 'Workload', wideOnly: true },
  { to: 'sprints', label: 'Sprints' },
  { to: 'docs', label: 'Docs' },
  { to: 'dashboard', label: 'Dashboard' },
  {
    to: 'settings',
    label: 'Settings',
    // Anyone who can change, archive or delete the project. A member who can
    // only read it is not shown a page on which everything is disabled.
    // The server re-checks per project regardless; this is the affordance.
    anyOf: [PERMISSIONS.PROJECT_UPDATE, PERMISSIONS.PROJECT_ARCHIVE, PERMISSIONS.PROJECT_DELETE],
  },
];

export function ProjectTabs() {
  const { key = '' } = useParams();
  const can = useCan();
  const listRef = useRef<HTMLElement>(null);

  const visible = TABS.filter((tab) => !tab.anyOf || tab.anyOf.some((p) => can(p)));

  /**
   * Arrow keys move between tabs, Home and End jump to the ends — the standard
   * tab-bar behaviour. Tab itself still steps through them, so this adds to
   * keyboard access rather than replacing it. Hidden tabs are skipped by
   * reading the DOM rather than the array: at narrow widths Timeline and
   * Workload are display:none, and arrowing onto one would focus nothing.
   */
  function onKeyDown(event: React.KeyboardEvent<HTMLElement>): void {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const links = [...(listRef.current?.querySelectorAll<HTMLAnchorElement>('a') ?? [])].filter(
      (a) => a.offsetParent !== null,
    );
    if (links.length === 0) return;

    const current = links.indexOf(document.activeElement as HTMLAnchorElement);
    event.preventDefault();

    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? links.length - 1
          : current === -1
            ? 0
            : (current + (event.key === 'ArrowRight' ? 1 : -1) + links.length) % links.length;

    links[next]?.focus();
  }

  if (!key) return null;

  return (
    <nav
      ref={listRef}
      aria-label="Project sections"
      data-testid="project-tabs"
      onKeyDown={onKeyDown}
      className="sticky top-0 z-30 flex gap-0.5 overflow-x-auto border-b border-[var(--border)] bg-[var(--bg)]/95 px-3 backdrop-blur"
    >
      {visible.map((tab) => (
        <NavLink
          key={tab.to}
          to={`/projects/${key}/${tab.to}`}
          data-testid={`project-tab-${tab.to}`}
          className={({ isActive }) =>
            cn(
              'relative shrink-0 whitespace-nowrap px-2.5 py-2 text-[13px] transition-colors duration-[120ms]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
              tab.wideOnly && 'hidden lg:block',
              isActive
                ? 'font-medium text-[var(--text)] after:absolute after:inset-x-2 after:bottom-0 after:h-[2px] after:rounded-full after:bg-[var(--accent)]'
                : 'vt-muted hover:text-[var(--text)]',
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}
